import { useCallback, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import DemandOverviewContent from './components/DemandOverviewContent';
import DemandOverviewProgress from './components/DemandOverviewProgress';
import DemandOverviewTabs from './components/DemandOverviewTabs';
import { DEMAND_OVERVIEW_DEFAULT_TAB_ID, DEMAND_OVERVIEW_TABS } from './enums/demandOverview';
import useDemandOverview from './hooks/useDemandOverview';
import {
  buildCreateDealURL,
  buildDemandOverviewMetricUrl,
  buildListingDealURL,
} from './utils/demandOverview/dealUrls';
import { openInNewTab } from './utils/demandOverview/openInNewTab';
import { useAppContext } from '~/AppProviders';

function DemandOverview() {
  const { realmName } = useAppContext();
  const [activeTabId, setActiveTabId] = useState(DEMAND_OVERVIEW_DEFAULT_TAB_ID);
  const activeTab = useMemo(
    () => DEMAND_OVERVIEW_TABS.find(tab => tab.id === activeTabId) || DEMAND_OVERVIEW_TABS[0],
    [activeTabId]
  );
  const { groups, isLoading, isFetching } = useDemandOverview(activeTabId);

  const handleTabChange = useCallback((_, nextTabId) => {
    setActiveTabId(nextTabId);
  }, []);

  const handleOpen = useCallback(
    metric => {
      openInNewTab(
        buildDemandOverviewMetricUrl(buildListingDealURL(activeTab.listingUrl, realmName), metric)
      );
    },
    [activeTab.listingUrl, realmName]
  );

  const handleCreate = useCallback(() => {
    openInNewTab(buildCreateDealURL(activeTab.createUrl, realmName));
  }, [activeTab, realmName]);

  const handleListView = useCallback(() => {
    openInNewTab(buildListingDealURL(activeTab.listingUrl, realmName));
  }, [activeTab, realmName]);

  return (
    <Paper
      sx={{ borderRadius: 2 }}
      elevation={2}
      className="sas-overview-card">
      <Box className="sas-overview-title">
        <Typography variant="h6">Demand Overview</Typography>
      </Box>

      <DemandOverviewTabs
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
      />

      <DemandOverviewProgress isVisible={isFetching && !isLoading} />

      <DemandOverviewContent
        activeTab={activeTab}
        groups={groups}
        isLoading={isLoading}
        onCreate={handleCreate}
        onListView={handleListView}
        onMetricOpen={handleOpen}
      />
    </Paper>
  );
}

export default DemandOverview;
